import { ArrowLeft } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  GlassCard,
  Modal,
  SettingsPageHeader,
  Pill,
  SurfacePageBody,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { BUTTON_GHOST, BUTTON_PRIMARY } from '@/shared/ui-class';
import {
  SEED_INSTALLED_PLUGINS,
  SEED_MARKETPLACE_PLUGINS,
  type MarketplacePluginSeed,
} from '@/versions/v1/shared/settings-seed';

type InstallPhase = 'idle' | 'installing' | 'done';

const INSTALL_DURATION_MS = 1400;

export function PluginMarketplaceScene() {
  const [marketplace, setMarketplace] = useState<MarketplacePluginSeed[]>(
    () => [...SEED_MARKETPLACE_PLUGINS],
  );
  const [query, setQuery] = useState('');
  const [detailEntry, setDetailEntry] = useState<MarketplacePluginSeed | null>(null);
  const [installPhase, setInstallPhase] = useState<InstallPhase>('idle');
  const [progressWidth, setProgressWidth] = useState(0);
  const installTimerRef = useRef<number | null>(null);
  const installingEntryIdRef = useRef<string | null>(null);

  const installedIds = useMemo(
    () => new Set(SEED_INSTALLED_PLUGINS.map((plugin) => plugin.marketplaceId)),
    [],
  );

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return marketplace;
    return marketplace.filter((entry) =>
      `${entry.name} ${entry.publisher} ${entry.description}`.toLowerCase().includes(needle),
    );
  }, [marketplace, query]);

  const clearInstallTimer = () => {
    if (installTimerRef.current != null) {
      window.clearTimeout(installTimerRef.current);
      installTimerRef.current = null;
    }
    installingEntryIdRef.current = null;
  };

  useEffect(() => () => clearInstallTimer(), []);

  useEffect(() => {
    if (installPhase !== 'installing') {
      setProgressWidth(0);
      return;
    }

    setProgressWidth(0);
    const frame = window.requestAnimationFrame(() => {
      setProgressWidth(100);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [installPhase]);

  const openDetail = (entry: MarketplacePluginSeed) => {
    clearInstallTimer();
    setDetailEntry(entry);
    setInstallPhase('idle');
    setProgressWidth(0);
  };

  const closeDetail = () => {
    clearInstallTimer();
    setDetailEntry(null);
    setInstallPhase('idle');
    setProgressWidth(0);
  };

  const handleInstall = () => {
    if (!detailEntry || installedIds.has(detailEntry.id) || detailEntry.installed) return;
    const entryId = detailEntry.id;
    clearInstallTimer();
    installingEntryIdRef.current = entryId;
    setInstallPhase('installing');
    installTimerRef.current = window.setTimeout(() => {
      installTimerRef.current = null;
      if (installingEntryIdRef.current !== entryId) return;
      setMarketplace((prev) =>
        prev.map((row) => (row.id === entryId ? { ...row, installed: true } : row)),
      );
      setInstallPhase('done');
    }, INSTALL_DURATION_MS);
  };

  const detailInstalled =
    detailEntry != null &&
    (detailEntry.installed || installedIds.has(detailEntry.id) || installPhase === 'done');

  return (
    <SurfacePageLayout width="narrow">
      <Commentable name="PluginMarketplaceScene">
        <SurfacePageBody className="space-y-6">
          <div>
            <Link
              to="/settings/plugins"
              className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted hover:text-zinc-900 dark:hover:text-zinc-100"
            >
              <ArrowLeft className="h-4 w-4" />
              Plugins
            </Link>
            <SettingsPageHeader
              title="Plugin marketplace"
              description="Install tool packs from publishers you trust."
            />
          </div>

          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search plugins"
            className="w-full rounded-lg border border-[var(--glass-border)] bg-[var(--glass-surface)] px-3 py-2 text-sm"
          />

          <div className="grid gap-4 sm:grid-cols-2">
            {filtered.map((entry) => {
              const isInstalled = entry.installed || installedIds.has(entry.id);
              return (
                <GlassCard key={entry.id} className="flex flex-col gap-3 p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <PluginLogo name={entry.name} />
                      <div>
                        <h3 className="font-medium">{entry.name}</h3>
                        <p className="text-xs text-muted">{entry.publisher}</p>
                      </div>
                    </div>
                    <Pill>v{entry.version}</Pill>
                  </div>
                  <p className="flex-1 text-sm text-muted">{entry.description}</p>
                  <button
                    type="button"
                    disabled={isInstalled}
                    onClick={() => openDetail(entry)}
                    className="rounded-lg border border-[var(--glass-border-strong)] px-3 py-2 text-sm font-medium disabled:opacity-50"
                  >
                    {isInstalled ? 'Installed' : 'Install'}
                  </button>
                </GlassCard>
              );
            })}
          </div>
        </SurfacePageBody>

        <Modal
          open={detailEntry != null}
          onOpenChange={(open) => {
            if (!open) closeDetail();
          }}
          title={detailEntry?.name ?? 'Plugin'}
        >
          {detailEntry ? (
            <div className="space-y-4 px-6 pb-6">
              <div className="flex items-start gap-3">
                <PluginLogo name={detailEntry.name} size="lg" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Pill>v{detailEntry.version}</Pill>
                  </div>
                  <p className="text-sm text-muted">{detailEntry.publisher}</p>
                </div>
              </div>

              <p className="text-sm leading-relaxed text-muted">{detailEntry.description}</p>

              {detailEntry.tools && detailEntry.tools.length > 0 ? (
                <GlassCard className="p-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted">
                    Tools included
                  </p>
                  <ul className="mt-2 space-y-1">
                    {detailEntry.tools.map((tool) => (
                      <li
                        key={tool}
                        className="font-mono text-sm text-zinc-700 dark:text-zinc-300"
                      >
                        {tool}
                      </li>
                    ))}
                  </ul>
                </GlassCard>
              ) : null}

              {installPhase === 'installing' ? (
                <div className="space-y-2">
                  <div className="h-2 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
                    <div
                      className="h-full rounded-full bg-[color-mix(in_srgb,var(--color-accent-600)_35%,transparent)] transition-[width] duration-[1400ms] ease-linear"
                      style={{ width: `${progressWidth}%` }}
                    />
                  </div>
                  <p className="text-sm text-muted">Installing plugin…</p>
                </div>
              ) : (
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={closeDetail} className={BUTTON_GHOST}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={detailInstalled}
                    onClick={handleInstall}
                    className={BUTTON_PRIMARY}
                  >
                    {detailInstalled ? 'Installed' : 'Install'}
                  </button>
                </div>
              )}
            </div>
          ) : null}
        </Modal>
      </Commentable>
    </SurfacePageLayout>
  );
}

function PluginLogo({ name, size = 'md' }: { name: string; size?: 'md' | 'lg' }) {
  const initial = name.trim().charAt(0).toUpperCase() || '?';
  const className =
    size === 'lg'
      ? 'flex h-12 w-12 items-center justify-center rounded-xl bg-zinc-100 text-lg font-semibold text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300'
      : 'flex h-10 w-10 items-center justify-center rounded-lg bg-zinc-100 text-sm font-semibold text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300';

  return <div className={className}>{initial}</div>;
}
